<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  sequence: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();
const dialogRef = ref(null);

const formState = ref({
  name: '',
  description: '',
  triggerEvent: 'manual',
  active: true,
  stopOnReply: true,
  stopOnClick: false,
  stopOnOptout: true,
});

const isSubmitting = ref(false);

const rules = {
  name: { required, minLength: minLength(3) },
};

const v$ = useVuelidate(rules, formState);

const isEditing = computed(() => !!props.sequence?.id);

const dialogTitle = computed(() =>
  isEditing.value
    ? t('CAMPAIGN.FOLLOW_UP.FORM.EDIT_TITLE')
    : t('CAMPAIGN.FOLLOW_UP.FORM.CREATE_TITLE')
);

const triggerOptions = computed(() => [
  { value: 'manual', label: t('CAMPAIGN.FOLLOW_UP.TRIGGER.MANUAL') },
  {
    value: 'campaign_sent',
    label: t('CAMPAIGN.FOLLOW_UP.TRIGGER.CAMPAIGN_SENT'),
  },
  {
    value: 'contact_replied',
    label: t('CAMPAIGN.FOLLOW_UP.TRIGGER.CONTACT_REPLIED'),
  },
  {
    value: 'link_clicked',
    label: t('CAMPAIGN.FOLLOW_UP.TRIGGER.LINK_CLICKED'),
  },
]);

watch(
  () => props.sequence,
  newSequence => {
    if (newSequence) {
      formState.value = {
        name: newSequence.name || '',
        description: newSequence.description || '',
        triggerEvent: newSequence.trigger_event || 'manual',
        active: newSequence.active ?? true,
        stopOnReply: newSequence.stop_on_reply ?? true,
        stopOnClick: newSequence.stop_on_click ?? false,
        stopOnOptout: newSequence.stop_on_optout ?? true,
      };
    }
  },
  { immediate: true }
);

const resetForm = () => {
  formState.value = {
    name: '',
    description: '',
    triggerEvent: 'manual',
    active: true,
    stopOnReply: true,
    stopOnClick: false,
    stopOnOptout: true,
  };
  v$.value.$reset();
};

const open = () => {
  if (!props.sequence) {
    resetForm();
  }
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;

  try {
    const payload = {
      name: formState.value.name,
      description: formState.value.description || null,
      trigger_event: formState.value.triggerEvent,
      active: formState.value.active,
      stop_on_reply: formState.value.stopOnReply,
      stop_on_click: formState.value.stopOnClick,
      stop_on_optout: formState.value.stopOnOptout,
    };

    if (isEditing.value) {
      payload.id = props.sequence.id;
    }

    emit('save', payload);
    close();
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    width="xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleClose"
  >
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <Input
        v-model="formState.name"
        :label="t('CAMPAIGN.FOLLOW_UP.FORM.NAME_LABEL')"
        :placeholder="t('CAMPAIGN.FOLLOW_UP.FORM.NAME_PLACEHOLDER')"
        :message="v$.name.$error ? t('CAMPAIGN.FOLLOW_UP.FORM.NAME_ERROR') : ''"
        :message-type="v$.name.$error ? 'error' : 'info'"
      />

      <TextArea
        v-model="formState.description"
        :label="t('CAMPAIGN.FOLLOW_UP.FORM.DESCRIPTION_LABEL')"
        :placeholder="t('CAMPAIGN.FOLLOW_UP.FORM.DESCRIPTION_PLACEHOLDER')"
        rows="3"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.FOLLOW_UP.FORM.TRIGGER_LABEL') }}
        </label>
        <ComboBox
          v-model="formState.triggerEvent"
          :options="triggerOptions"
          class="[&>div>button]:bg-n-alpha-black2"
        />
        <p class="text-xs text-n-slate-11 mt-1">
          {{ t('CAMPAIGN.FOLLOW_UP.FORM.TRIGGER_HELP') }}
        </p>
      </div>

      <div class="flex items-center justify-between">
        <div>
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.FOLLOW_UP.FORM.ACTIVE_LABEL') }}
          </span>
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.FOLLOW_UP.FORM.ACTIVE_DESC') }}
          </p>
        </div>
        <Switch v-model="formState.active" />
      </div>

      <div class="p-4 rounded-lg bg-n-alpha-2 space-y-3">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.FOLLOW_UP.FORM.STOP_CONDITIONS') }}
        </h4>
        <p class="text-xs text-n-slate-11 -mt-1">
          {{ t('CAMPAIGN.FOLLOW_UP.FORM.STOP_CONDITIONS_DESC') }}
        </p>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="i-lucide-message-circle size-4 text-n-slate-11" />
            <span class="text-sm text-n-slate-12">
              {{ t('CAMPAIGN.FOLLOW_UP.FORM.STOP_REPLY') }}
            </span>
          </div>
          <Switch v-model="formState.stopOnReply" />
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="i-lucide-mouse-pointer-click size-4 text-n-slate-11" />
            <span class="text-sm text-n-slate-12">
              {{ t('CAMPAIGN.FOLLOW_UP.FORM.STOP_CLICK') }}
            </span>
          </div>
          <Switch v-model="formState.stopOnClick" />
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="i-lucide-ban size-4 text-n-slate-11" />
            <span class="text-sm text-n-slate-12">
              {{ t('CAMPAIGN.FOLLOW_UP.FORM.STOP_OPTOUT') }}
            </span>
          </div>
          <Switch v-model="formState.stopOnOptout" />
        </div>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button type="button" variant="faded" color="slate" @click="close">
          {{ t('CAMPAIGN.FOLLOW_UP.FORM.CANCEL') }}
        </Button>
        <Button type="submit" :is-loading="isSubmitting">
          {{
            isEditing
              ? t('CAMPAIGN.FOLLOW_UP.FORM.SAVE_UPDATE')
              : t('CAMPAIGN.FOLLOW_UP.FORM.SAVE_CREATE')
          }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
